// The Home card's way into the sales enablement deck (v1, 2026-10-23): a deck
// belongs to one project, so this picks the project and opens its "Sales deck"
// step on the project rail. Only Deal Accelerator, Decision Builder+ and
// Demand Cascade projects get a deck — the others are counted, not listed
// (the step itself explains why, on the project).
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import CiqStageChip from '../../components/CiqStageChip';
import { DECK_STATUS } from './SalesDeckPanel';
import {
  FONTS, HUES, INK, NEUTRAL, RADII, STATE,
  buttonLink, buttonPrimarySmall, card, pageShell, pill,
} from '../../utils/wizardTheme';

const API = process.env.REACT_APP_API_URL;
const HUE = HUES.scripts;

// Projects joined with their deck standing; deck-able ones first, recently
// worked first (the order the projects list already arrives in).
export function deckRows(projects, decks) {
  const byId = Object.fromEntries((decks || []).map(d => [d.project_id, d]));
  return (projects || []).map(pj => ({ pj, deck: byId[pj.id] || { has_deck: false } }));
}

export default function ContentIQDeckStart() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/contentiq/projects/`),
      axios.get(`${API}/contentiq/decks`),
    ]).then(([p, d]) => setRows(deckRows(p.data, d.data))).catch(() => setErr(true));
  }, []);

  const withDeck = (rows || []).filter(r => r.deck.has_deck);
  const without = (rows || []).length - withDeck.length;

  return (
    <div style={pageShell}>
      <div style={{
        maxWidth: '760px', margin: '0 auto', padding: '36px 32px',
        boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '20px',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button onClick={() => navigate('/contentiq')}
            style={{ ...buttonLink, fontSize: '13px', alignSelf: 'flex-start' }}>
            ← ContentIQ Home
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '44px', height: '44px', borderRadius: RADII.tile, background: HUE.main,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: NEUTRAL.pageBg, fontFamily: FONTS.display, fontWeight: 700, fontSize: '20px',
            }}>S</div>
            <div style={{ fontFamily: FONTS.display, fontSize: '27px', fontWeight: 700 }}>Sales enablement deck</div>
          </div>
          <div style={{ fontSize: '13px', color: INK.secondary, lineHeight: 1.55 }}>
            A follow-up guide for the client’s sales team, built from the project’s confirmed facts, its strategy
            and the built pages. Deal Accelerator, Decision Builder+ and Demand Cascade projects have one — pick
            the project.
          </div>
        </div>

        {err && (
          <div role="alert" style={{ fontSize: '13px', color: STATE.danger.text }}>
            Couldn’t load your projects — refresh the page to try again.
          </div>
        )}
        {!rows && !err && <div style={{ fontSize: '13px', color: INK.muted }}>Loading projects…</div>}

        {rows && withDeck.length === 0 && (
          <div style={{ ...card, padding: '16px 20px', fontSize: '13px', color: INK.body, lineHeight: 1.55 }}>
            No Deal Accelerator, Decision Builder+ or Demand Cascade projects yet. A deck comes from a project —{' '}
            <button onClick={() => navigate('/contentiq/new')} style={{ ...buttonLink, fontSize: '13px' }}>
              start one
            </button>.
          </div>
        )}

        {withDeck.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {withDeck.map(({ pj, deck }) => {
              const att = (pj.attachments || [])[0];
              const st = deck.built ? (DECK_STATUS[deck.status] || DECK_STATUS.draft) : null;
              return (
                <div key={pj.id} style={{ ...card, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flexGrow: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '14px' }}>{pj.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: INK.secondary, flexWrap: 'wrap' }}>
                      {att && <span>{att.client_name ? `${att.client_name} · ` : ''}{att.program_name}</span>}
                      <span style={pill(HUE.deep, HUE.tint)}>{deck.product}</span>
                      {pj.stage && <CiqStageChip stage={pj.stage} />}
                      <span style={st ? pill(st.fg, st.bg) : pill(INK.secondary, NEUTRAL.chipBg)}>
                        {st ? `Deck: ${st.label}` : 'No deck yet'}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => navigate(`/contentiq/project/${pj.id}?section=deck`)}
                    style={buttonPrimarySmall(HUE.main)}>
                    {deck.built ? 'Open the deck →' : 'Start the deck →'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {rows && without > 0 && (
          <div style={{ fontSize: '12px', color: INK.muted }}>
            {without} other project{without === 1 ? ' has' : 's have'} no sales deck — Content Syndication,
            Decision Builder, or no product picked yet.
          </div>
        )}
      </div>
    </div>
  );
}
